import React from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";

import { InlineEditText } from "../../../components/InlineEditText";
import {
  useDeleteProfileType,
  useProfileTypes,
  useUpdateProfileType,
} from "../hooks/useTeacherRates";

/**
 * Gestión del catálogo de "Tipos de perfil" de la academia (Profesional,
 * Practicante y los que añada el propietario desde el formulario de tarifas).
 *
 * Renombrar: clic en el nombre (InlineEditText) → PATCH /profile-types/<id>/.
 * Eliminar: botón → DELETE /profile-types/<id>/. El backend responde 409 si un
 * docente todavía lo tiene asignado; en ese caso se muestra su mensaje.
 */
function ProfileTypesModal({ show, onHide }) {
  const { data: types = [], isLoading, isError } = useProfileTypes();
  const updateMutation = useUpdateProfileType();
  const deleteMutation = useDeleteProfileType();

  const handleRename = (type) => async (name) => {
    // InlineEditText espera una promesa: si rechaza, conserva la edición y
    // muestra field_errors.name / detail del backend.
    await updateMutation.mutateAsync({ id: type.id, name });
    toast.success("Tipo de perfil actualizado");
  };

  const handleDelete = (type) => {
    if (
      !window.confirm(
        `¿Eliminar el tipo de perfil "${type.name}"? Las tarifas por este tipo quedarán sin tipo asignado.`
      )
    )
      return;
    deleteMutation.mutate(type.id, {
      onSuccess: () => toast.success("Tipo de perfil eliminado"),
      onError: (err) =>
        toast.error(
          err?.response?.data?.detail ??
            err?.response?.data?.field_errors?.detail?.[0] ??
            "No se pudo eliminar el tipo de perfil."
        ),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Tipos de perfil</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Se usan para definir una tarifa por defecto según el perfil del docente
          (p. ej. Profesional o Practicante). Para crear uno nuevo, escríbelo en
          el selector “Tipo de perfil” al registrar una tarifa.
        </p>

        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {isError && (
          <Alert variant="danger" className="mb-0">
            No se pudieron cargar los tipos de perfil.
          </Alert>
        )}
        {!isLoading && !isError && types.length === 0 && (
          <p className="text-muted text-center py-3 mb-0">
            Aún no hay tipos de perfil.
          </p>
        )}

        {types.length > 0 && (
          <ul className="list-group">
            {types.map((type) => (
              <li
                key={type.id}
                className="list-group-item d-flex justify-content-between align-items-center gap-2"
              >
                <InlineEditText
                  value={type.name}
                  onSave={handleRename(type)}
                  ariaLabel={`Renombrar ${type.name}`}
                  maxLength={100}
                  disabled={deleteMutation.isPending}
                />
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={{ minHeight: 44 }}
                  onClick={() => handleDelete(type)}
                  disabled={deleteMutation.isPending}
                >
                  Eliminar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} style={{ minHeight: 44 }}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { ProfileTypesModal };
